import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { RULE_FIELDS, ruleRangeText, rulesChange, rulesForm, type SensorRuleError, type SensorRules, type SensorRulesForm } from "./sensorRules";
import { SkeletonSection } from "./Skeleton";

/** Design 8.10: the limits of the sensor rules that open issues each night, for the whole organization. */
export function SensorRuleSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.SensorRuleSettings | "failed" | undefined>(undefined);
  const [form, setForm] = useState<SensorRulesForm | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<SensorRuleError | null>(null);
  const [undo, setUndo] = useState<SensorRules | null>(null);
  const status = useRef<HTMLParagraphElement>(null);
  const inputs = useRef<Partial<Record<keyof SensorRules, HTMLInputElement | null>>>({});

  useEffect(() => {
    if (problem) inputs.current[problem.key]?.focus();
  }, [problem]);

  useEffect(() => {
    user
      .getIdToken()
      .then(api.sensorRules)
      .then(
        (rules) => {
          setFound(rules);
          setForm(rulesForm(rules.limits));
        },
        () => setFound("failed"),
      );
  }, [user]);

  if (found === undefined || form === null) {
    return found === "failed" ? (
      <fieldset>
        <legend>Sensor rules</legend>
        <p className="error">Cannot load the sensor rules. Try again later.</p>
      </fieldset>
    ) : (
      <SkeletonSection title="Sensor rules" rows={15} />
    );
  }
  if (found === "failed") return null;
  const current = found;
  const send = async (rules: SensorRules, done: string, before: SensorRules | null) => {
    setSaving(true);
    setMessage(null);
    setUndo(null);
    try {
      const next = await api.setSensorRules(await user.getIdToken(), rules);
      setFound(next);
      setForm(rulesForm(next.limits));
      setMessage({ ok: true, text: done });
      setUndo(before);
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The sensor rules were not saved. Try again." });
    } finally {
      setSaving(false);
      if (before === null) status.current?.focus();
    }
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const rules = rulesChange(form);
    if ("text" in rules) {
      setMessage(null);
      setProblem(rules);
      return;
    }
    setProblem(null);
    await send(rules, "Saved. The next nightly run uses the new limits.", current.limits);
  };
  const defaults = rulesForm(current.defaults);
  return (
    <form onSubmit={save} noValidate data-testid="sensor-rules">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Sensor rules</legend>
        <p className="hint">Each night the rules check the sensors and open an issue for each new finding.</p>
        {RULE_FIELDS.map(({ key, label, unit, hint }) => {
          const wrong = problem?.key === key;
          return (
            <div key={key} className="limit">
              <label htmlFor={`rule-${key}`}>
                {label} ({unit})
              </label>
              <input
                id={`rule-${key}`}
                ref={(element) => {
                  inputs.current[key] = element;
                }}
                data-testid={`rule-${key}`}
                inputMode="decimal"
                value={form[key]}
                aria-invalid={wrong || undefined}
                aria-describedby={wrong ? `rule-${key}-error rule-${key}-hint` : `rule-${key}-hint`}
                onChange={(e) => {
                  setForm({ ...form, [key]: e.target.value });
                  setMessage(null);
                  setUndo(null);
                  if (wrong) setProblem(null);
                }}
              />
              {wrong && (
                <p id={`rule-${key}-error`} role="alert" className="error" data-testid={`rule-${key}-error`}>
                  {problem?.text}
                </p>
              )}
              <p id={`rule-${key}-hint`} className="hint">
                {hint} {ruleRangeText(key, defaults)}
              </p>
            </div>
          );
        })}
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-rules">
              {saving ? "Saving…" : "Save the sensor rules"}
            </button>
            <button
              type="button"
              data-testid="default-rules"
              onClick={() => {
                setForm(defaults);
                setProblem(null);
                setUndo(null);
                setMessage({ ok: true, text: "The default values are in the form. Press Save the sensor rules to use them." });
              }}
            >
              Use the defaults
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="rules-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
        {undo && found.can_edit && (
          <>
            {" "}
            <button type="button" data-testid="undo-rules" disabled={saving} onClick={() => void send(undo, "Undone.", null)}>
              Undo
            </button>
          </>
        )}
      </p>
    </form>
  );
}
